import { useContext } from "react";
import { Link } from "react-router-dom";
import useStructureParams from "../lib/useStructureParams";
import { filterAndSortChildren, wardNamesCleanup } from "../lib/structures";
import useSummaryData from "../data/useSummaryData";

import PageTitle from "./PageTitle";
import Breadcrumbs from "./Breadcrumbs";
import PageHeader from "./PageHeader";
import ViewButtons from "./ViewButtons";
import { LoadingSpinner } from "./spinners";
import { NoAssignedWardsMessage } from "./alerts";
import ResponsiblePersons from "./ResponsiblePersons";
import { statsStyle, formatInt, formatPerc } from "../lib/utils";

import Table from "react-bootstrap/Table";
import VDCategoryFilter from "./VDCategoryFilter";
import { DashboardContext } from "../data/context";
import HeaderLayout from "./HeaderLayout";

export default function SummaryView(): JSX.Element {
  const { filterLabel } = useContext(DashboardContext);
  // get parameters from the URL
  const { code, type } = useStructureParams();

  // get stats from the GraphQL API
  const { stats, loading } = useSummaryData({ type, code, filterLabel });

  // this happens while the query is still fetching
  if (stats == null || loading) {
    return <LoadingSpinner />;
  }

  // filter/sort children appropriately according to structure type
  const children = filterAndSortChildren(stats.type, stats.children)
    .map(wardNamesCleanup)
    .filter((c) => c.structureHasAssignedWards);

  if (!stats.structureHasAssignedWards) {
    return (
      <>
        <PageTitle title={stats.name} />
        <Breadcrumbs structure={stats} />
        {/* <PageHeader structure={stats} />
        <VDCategoryFilter counts={stats.counts} />
        <ViewButtons structure={stats} section="summary" /> */}
        <HeaderLayout
           element1={<PageHeader structure={stats} />}
           element2={<ViewButtons structure={stats} section="summary" />}
           element3={<VDCategoryFilter counts={stats.counts} />}
        />

        <NoAssignedWardsMessage type={stats.type} />
      </>
    );
  } else {
    return (
      <>
        <PageTitle title={stats.name} />
        <Breadcrumbs structure={stats} />
        {/* <PageHeader structure={stats} />
        <VDCategoryFilter counts={stats.counts} />
        <ViewButtons structure={stats} section="summary" /> */}
        <HeaderLayout
           element1={<PageHeader structure={stats} />}
           element2={<ViewButtons structure={stats} section="summary" />}
           element3={<VDCategoryFilter counts={stats.counts} />}
        />

        <Table responsive striped bordered className="stats">
          <thead>
            <tr>
              <th />
              <th className="text-center">VD Counts</th>
              <th className="text-center">
                Registrations as a % of where we should be
              </th>
              <th className="text-center">% of Registration Target Success</th>
              <th className="text-center">
                Canvasses as a % of Where We Should Be
              </th>
              <th className="text-center">
                Canvasses as a % of Unique Affiliation Canvasses Target
              </th>
              <th className="text-center">
                Unique First Survey Achieved as a % of Target <br /> (1 Nov 2025
                to 17 May 2026)
              </th>
            </tr>
          </thead>
          <tbody>
            {children.map((c) => {
              return (
                <tr key={c.code}>
                  {stats.type !== "ward" ? (
                    <td>
                      <Link to={`/${c.type}/${c.code}`}>{c.name}</Link>{" "}
                      <ResponsiblePersons persons={c.responsiblePersons} />
                    </td>
                  ) : (
                    <td>{c.name}</td>
                  )}
                  <td className="text-center">{`${formatInt(c.counts.filtered)} of ${formatInt(c.counts.total)}`}</td>
                  <td
                    style={statsStyle(
                      c.registration.rollAll,
                      c.registration.milestone,
                    )}
                    className="text-center"
                  >
                    {formatPerc(
                      c.registration.rollAll / c.registration.milestone,
                    )}
                  </td>
                  <td
                    style={statsStyle(
                      c.registration.rollAll,
                      c.registration.target,
                    )}
                    className="text-center"
                  >
                    {" "}
                    {formatPerc(c.registration.rollAll / c.registration.target)}
                  </td>
                  <td
                    style={statsStyle(
                      c.canvassing.current,
                      c.canvassing.milestone,
                    )}
                    className="text-center"
                  >
                    {formatPerc(c.canvassing.current / c.canvassing.milestone)}
                  </td>
                  <td
                    style={statsStyle(
                      c.canvassing.current,
                      c.canvassing.target,
                    )}
                    className="text-center"
                  >
                    {formatPerc(c.canvassing.current / c.canvassing.target)}
                  </td>
                  <td
                    style={statsStyle(
                      c.microtargeting.phase1.current,
                      c.microtargeting.phase1.target,
                    )}
                    className="text-center"
                  >
                    {formatPerc(
                      c.microtargeting.phase1.current /
                        c.microtargeting.phase1.target,
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="font-weight-bold">
              <td>Total</td>
              <td className="text-center">{`${formatInt(stats.counts.filtered)} of ${formatInt(stats.counts.total)}`}</td>
              <td
                style={statsStyle(
                  stats.registration.rollAll,
                  stats.registration.milestone,
                )}
                className="text-center"
              >
                {formatPerc(
                  stats.registration.rollAll / stats.registration.milestone,
                )}
              </td>
              <td
                style={statsStyle(
                  stats.registration.rollAll,
                  stats.registration.target,
                )}
                className="text-center"
              >
                {formatPerc(
                  stats.registration.rollAll / stats.registration.target,
                )}
              </td>
              <td
                style={statsStyle(
                  stats.canvassing.current,
                  stats.canvassing.milestone,
                )}
                className="text-center"
              >
                {formatPerc(
                  stats.canvassing.current / stats.canvassing.milestone,
                )}
              </td>
              <td
                style={statsStyle(
                  stats.canvassing.current,
                  stats.canvassing.target,
                )}
                className="text-center"
              >
                {formatPerc(stats.canvassing.current / stats.canvassing.target)}
              </td>
              <td
                style={statsStyle(
                  stats.microtargeting.phase1.current,
                  stats.microtargeting.phase1.target,
                )}
                className="text-center"
              >
                {formatPerc(
                  stats.microtargeting.phase1.current /
                    stats.microtargeting.phase1.target,
                )}
              </td>
            </tr>
          </tfoot>
        </Table>
      </>
    );
  }
}
